import {
  Button, Modal, Pagination
} from '@mui/material';
import { Box } from '@mui/system';
import React, {
  useEffect, useState
} from 'react';
import './BulkApproveRejectUsers.modal.scss';
import {
  DataGrid, GridCellParams, GridSelectionModel
} from '@mui/x-data-grid';
import { User } from '../../features/user/user.model';
import SubmitButton from '../forms/SubmitButton';
import { TableState } from '../../constants/tables/table.types';
import { bulkApproveRejectUsersListColumns } from '../../constants/tables/userTable.types';
import { setAlert } from '../../features/alert/alert.slice';
import { useAppDispatch } from '../../app/hooks';
import {
  approveStudent, rejectStudent
} from '../../features/user/user.thunks';


interface BulkApproveRejectUsersModalProps {
  parentSelectionModel: GridSelectionModel;
  setSelectionModelCallback: (newSelectionModel: GridSelectionModel) => void;
  selectedUsers: User[];
  setSelectedUsersCallback: (newUserSelection: User[]) => void;
  open: boolean;
  approving: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}


const BulkApproveRejectUsersModal: React.FC<BulkApproveRejectUsersModalProps> = ({
  parentSelectionModel,
  setSelectionModelCallback,
  selectedUsers,
  setSelectedUsersCallback,
  open,
  approving,
  onClose,
  onSuccess,
}) => {

  const dispatch = useAppDispatch();

  const [
    tableState,
    setTableState,
  ] = useState<TableState>({
    page: 1,
    pageSize: 100,
    columns: bulkApproveRejectUsersListColumns,
    rows: [],
    loading: false,
  });

  useEffect(() => {
    const removePage = tableState.page > Math.ceil(selectedUsers.length / tableState.pageSize) && tableState.page > 1;

    setTableState((prev) => ({
      ...prev,
      page: removePage ? prev.page - 1 : prev.page,
      rows: removePage ? getNewRows(tableState.page - 2) : getNewRows(tableState.page - 1),
    }));
  }, [
    selectedUsers,
  ]);

  const getNewRows = (page: number) => {
    return selectedUsers.slice(page * tableState.pageSize, (page + 1) * tableState.pageSize);
  };

  const submitForm = async () => {
    const approvalIds = selectedUsers
      .map(({ status }) => status?.approvalId)
      .filter((approvalId): approvalId is number => approvalId !== undefined);

    if (!approvalIds.length) return;

    const thunk = approving ? approveStudent : rejectStudent;
    const actionLabel = approving ? 'approved' : 'rejected';

    dispatch(thunk(approvalIds)).unwrap()
      .then(() => {
        dispatch(setAlert({
          type: 'success',
          message: `Successfully bulk ${actionLabel} users`,
        }));
        setSelectionModelCallback([]);
        setSelectedUsersCallback([]);
        onSuccess?.();
      })
      .catch(() => {
        dispatch(setAlert({
          type: 'error',
          message: `Unable to bulk ${actionLabel} users`,
        }));
      });

    onClose();
  };


  return (
    <Modal
      id="bulk-approve-reject-users-modal"
      open={open}
      onClose={onClose}
    >
      <Box className="modal-content card-background flex_col_jcenter">
        <form className="content-container flex_col_acenter" onSubmit={submitForm}>
          <h3>{`Would you like to ${approving ? 'approve' : 'reject'} these users?`}</h3>

          <DataGrid
            className="table"
            columns={tableState.columns}
            rows={tableState.rows}
            components={{
              Pagination: Pagination,
            }}
            componentsProps={{
              pagination: {
                page: tableState.page,
                count: Math.ceil(selectedUsers.length / tableState.pageSize),
                onChange: (_: void, page: number) => {
                  setTableState((prev) => ({
                    ...prev,
                    page,
                    rows: getNewRows(page - 1),
                  }));
                },
              },
            }}
            onCellClick={({ field, id }: GridCellParams) => {
              if (field === 'delete') {
                setSelectedUsersCallback(selectedUsers.filter(({ id: userId }) => userId !== id));
                setSelectionModelCallback(parentSelectionModel.filter((userId) => userId !== id));
              }
            }}
            pagination
            disableColumnFilter
            disableColumnMenu
            disableSelectionOnClick
            hideFooterSelectedRowCount
          />

          <div className="modal-buttons flex_jbetween">
            <Button
              variant="outlined"
              onClick={onClose}
            >
              Cancel
            </Button>
            <SubmitButton
              text={approving ? 'Approve' : 'Reject'}
              variant="contained"
              disableElevation
              disabled={!selectedUsers.length}
            />
          </div>
        </form>
      </Box>
    </Modal>
  );
};

export default BulkApproveRejectUsersModal;
